//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext } from 'react';
import { makeStyles } from '@material-ui/core';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { BASE_URL, SNACK_BAR_MESSAGE_PASSWORD_SAVED } from '../../common/constants';
import { checkPasswordStrength } from '../../common/authHelper';

import { REQUIRED, PASSWORDS_DO_NOT_MATCH, ERROR_MESSAGE_WEAK_PASSWORD, AUTH_ERROR_CODE_WRONG_PASSWORD } from '../../common/constants';
import { PLACEHOLDER_FIELD_CONFIRM_PASSWORD, PLACEHOLDER_FIELD_NEW_PASSWORD, PLACEHOLDER_FIELD_CURRENT_PASSWORD } from '../../common/placeholderHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { changeCurrentUserPassword } from '../../common/services/authServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';
import _set from 'lodash/set';
import { PATH_FORGOT_PASSWORD } from '../../system/constants';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    avatarPlaceholder: {
        width: theme.spacing(8),
        height: theme.spacing(8),
        borderRadius: '100%',
    },
    avatarPlaceholderBox: {
        width: 104,
        height: 104,
        background: '#FFFFFF',
        border: '1px dashed #D5D7DD',
        borderRadius: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
    },
    btnBox: {
        padding: 10,
        paddingTop: 30,
        [theme.breakpoints.down('sm')]: { paddingLeft: 0 },
    },
    instructionText: {
        marginTop: 5,
        color: 'rgba(17, 30, 20, 0.4)',
    },
    inputBoxContainer: {
        display: 'flex',
        margin: '10px 0',
        [theme.breakpoints.down('sm')]: { flexDirection: 'column', margin: '0px' },
    },
    inputBox: {
        flex: 1,
        padding: '0 5px',
        [theme.breakpoints.down('sm')]: { padding: '12px 0px' },
    },
    label: {
        padding: '5px 0',
        fontWeight: 'bold',
    },
    dropDown: {
        fontSize: '16px',
        color: '#1D8A8B',
        border: '1px solid #D5D7DD',
        borderRadius: 4,
    },
    focusDropDown: {
        fontSize: '16px',
        color: '#1D8A8B',
        border: '2px solid #1D8A8B',
        borderRadius: 4,
    },
    buttonBox: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '10px 0',
        marginTop: 16,
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column-reverse',
            justifyContent: 'center',
            padding: '0px',
            marginTop: 16,
        },
    },
    heading: {
        fontWeight: 'bold',
    },

    rightMarginToLeftButton: { marginRight: 16 },
    generalAvatarContainer: { display: 'flex', margin: '30px 0', [theme.breakpoints.down('sm')]: { flexDirection: 'column' } },
    hidden: { display: 'none' },
    cropContainer: { height: 250, width: '100%', textAlign: 'center' },
    imgBeforeCropContainer: { display: 'flex', justifyContent: 'center' },
    uploadLinearProgressContainer: { marginTop: 30, padding: '0 20px' },
    generalCancelButton: { marginRight: 16, [theme.breakpoints.down('sm')]: { width: '100%', marginRight: 0 } },
    generalSaveButton: { [theme.breakpoints.down('sm')]: { width: '100%', marginBottom: 16 } },
    inputBoxes: { marginTop: 30 },
    newPasswordInput: { marginTop: 10 },
    currentPasswordError: {
        fontSize: '12px',
        color: 'gray',
        marginLeft: '10px',
    },
    newPasswordError: {
        fontSize: '12px',
        color: 'gray',
        marginLeft: '10px',
    },
    confirmNewPasswordError: {
        fontSize: '12px',
        color: 'gray',
        marginLeft: '10px',
    },
    forgotPasswordContainer: { marginTop: 5 },
    forgotPasswordLink: { marginBottom: 16 },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, formik, handleClickForgotPasswordLink } = params;

    const defns = {
        generalCancelButton: { disabled: false }, //className: rightMarginToLeftButtonCls
        generalSaveButton: {
            disabled: formik.values.confirmNewPassword === '' && formik.values.currentPassword === '' && formik.values.newPassword === '',
            color: 'primary',
            type: 'submit',
        },

        heading: { variant: 'h6' },
        label: { variant: 'body2' },
        currentPassword: {
            placeholder: PLACEHOLDER_FIELD_CURRENT_PASSWORD,
            type: 'password',
            id: 'currentPassword',
            ...formik.getFieldProps('currentPassword'),
        },
        currentPasswordError: {
            value: formik.touched.currentPassword && formik.errors?.currentPassword,
        },
        newPassword: {
            placeholder: PLACEHOLDER_FIELD_NEW_PASSWORD,
            type: 'password',
            id: 'newPassword',
            ...formik.getFieldProps('newPassword'),
        },
        newPasswordError: {
            value: formik.touched.newPassword && formik.errors?.newPassword,
        },

        confirmNewPassword: {
            placeholder: PLACEHOLDER_FIELD_CONFIRM_PASSWORD,
            type: 'password',
            id: 'confirmNewPassword',
            ...formik.getFieldProps('confirmNewPassword'),
        },
        confirmPasswordError: {
            value: formik.touched.confirmNewPassword && formik.errors?.confirmNewPassword,
        },

        forgotPasswordLink: {
            color: 'secondary',
            onClick: handleClickForgotPasswordLink,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};
//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const validate = values => {
    const errors = {};
    const fieldNames = ['currentPassword', 'newPassword', 'confirmNewPassword'];

    // required fields for login and sign up
    _forEach(fieldNames, fieldName => {
        if (!_get(values, fieldName)) {
            _set(errors, fieldName, REQUIRED);
        }
    });

    if (!errors.newPassword) {
        if (!checkPasswordStrength(_get(values, 'newPassword'))) {
            errors.newPassword = ERROR_MESSAGE_WEAK_PASSWORD;
        }
    }
    // password must equal confirm password
    if (!errors.newPassword && !errors.confirmNewPassword) {
        if (values.newPassword !== values.confirmNewPassword) {
            errors.newPassword = errors.confirmNewPassword = PASSWORDS_DO_NOT_MATCH;
        }
    }

    return errors;
};
//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ChangePassword = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const { setSnackbarMessage, setSnackbarErrorMessage } = useContext(LayoutContext);
    const history = useHistory();

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Formik
    //---------------------------------------------------------------------
    const formik = useFormik({
        initialValues: {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: '',
        },
        validate: validate.bind(null),
        validateOnBlur: false,
        validateOnChange: true,
        onSubmit: async values => onSubmit(values),
    });

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const updatePassword = () => {
        setSnackbarMessage(SNACK_BAR_MESSAGE_PASSWORD_SAVED);
    };

    const onSubmit = async values => {
        try {
            const response = await changeCurrentUserPassword({
                newPassword: _get(values, 'newPassword'),
                currentPassword: _get(values, 'currentPassword'),
                setSnackbarMessage,
                setSnackbarErrorMessage,
                formik,
            });
            if (response) {
                const { error } = response;
                if (error && error?.code === AUTH_ERROR_CODE_WRONG_PASSWORD) {
                    setSnackbarErrorMessage('Your Current password entered do not match our record');
                }
            }
        } catch (authError) {
            console.log({ authError });
        }
    };

    const handleClickForgotPasswordLink = () => {
        history.push(`${BASE_URL}${PATH_FORGOT_PASSWORD}`);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        updatePassword,
        formik,
        handleClickForgotPasswordLink,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            <AfitdTypography {...defns.heading}>Change Password</AfitdTypography>
            <form onSubmit={formik.handleSubmit}>
                <div {...defns.inputBoxes}>
                    <div {...defns.newPasswordInput}>
                        <AfitdTypography {...defns.label}>Current Password</AfitdTypography>
                        <AfitdTextField {...defns.currentPassword} />
                        <AfitdTypography {...defns.currentPasswordError} />
                    </div>
                    <div {...defns.forgotPasswordContainer}>
                        <AfitdLink {...defns.forgotPasswordLink} label="Forgot Password?" />
                    </div>
                    <div {...defns.newPasswordInput}>
                        <AfitdTypography {...defns.label}>New Password</AfitdTypography>
                        <AfitdTextField {...defns.newPassword} />
                        <AfitdTypography {...defns.newPasswordError} />
                    </div>
                    <div {...defns.newPasswordInput}>
                        <AfitdTypography {...defns.label}>New Confirm Password</AfitdTypography>
                        <AfitdTextField {...defns.confirmNewPassword} />
                        <AfitdTypography {...defns.confirmNewPasswordError} />
                    </div>
                </div>

                <div className={classes.buttonBox}>
                    <AfitdButton {...defns.generalCancelButton}>Cancel</AfitdButton>
                    <AfitdButton {...defns.generalSaveButton}>Update Password</AfitdButton>
                </div>
            </form>
        </>
    );
};

export default ChangePassword;
